import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { CustomMaterialData } from 'projects/phdsoft/common/src/lib/models/basic-element.model'
import { PendingMaterialChange } from './material-edit.model'
import * as THREE from '@phdsoft/three'
import { StructuralElement } from 'src/app/platform/busines-data/SectionElements/StructureElements/StructuralElement'
import { Graphics } from 'src/app/digital-twin/graphics/graphics'

@Injectable({ providedIn: 'root' })
export class MaterialEditService {
  private _pendingChanges = new Map<string, PendingMaterialChange>()
  private _pendingCount$ = new BehaviorSubject<number>(0)
  public pendingCount$ = this._pendingCount$.asObservable()

  public paintActive = false
  public paintBrush: CustomMaterialData | null = null
  /**
   * The element whose material seeded the paint brush. Clicking the anchor
   * during paint is a no-op — its pending entry is "locked" against the
   * paint-toggle so the user can't accidentally remove the source from the
   * change list.
   */
  public paintAnchorElementId: string | null = null

  constructor(private graphics: Graphics) {}

  get pendingCount(): number {
    return this._pendingChanges.size
  }

  activatePaint(brush: CustomMaterialData, anchorElementId: string | null = null): void {
    this.paintBrush = brush
    this.paintActive = true
    this.paintAnchorElementId = anchorElementId
  }

  deactivatePaint(): void {
    this.paintActive = false
    this.paintBrush = null
    this.paintAnchorElementId = null
  }

  applyBrushToElement(element: StructuralElement): void {
    if (!this.paintBrush || !element.SurfaceDesign) return

    const brush = this.paintBrush
    const material = new THREE.MeshStandardMaterial({
      color: brush.Color,
      opacity: brush.Opacity,
      transparent: brush.Opacity < 1.0,
      roughness: brush.Roughness,
      metalness: brush.Metalness,
      side: THREE.DoubleSide,
    })
    if (this.graphics.customMaterialEnvMap) {
      material.envMap = this.graphics.customMaterialEnvMap
      material.envMapIntensity = 0.3
    }

    const existing = this._pendingChanges.get(element.Guid)
    const originalMaterial = existing
      ? existing.originalMaterial
      : element.defaultMaterial

    this.storePendingChange({
      elementId: element.Guid,
      regionId: element.regionId,
      sectionId: (element as any).properties?.SectionId ?? element.SectionId,
      materialData: brush,
      previewMaterial: material,
      originalMaterial,
      elementRef: element,
    })

    element.currentRenderMaterial = material
  }

  storePendingChange(change: PendingMaterialChange): void {
    const existing = this._pendingChanges.get(change.elementId)
    if (existing && existing.previewMaterial && existing.previewMaterial !== change.previewMaterial) {
      existing.previewMaterial.dispose()
    }
    this._pendingChanges.set(change.elementId, change)
    this._pendingCount$.next(this._pendingChanges.size)
  }

  /**
   * Records that the element should revert to its type-default material on the next
   * Save All. Used by the Default toolbar button. The previously saved custom
   * material is captured as originalMaterial so Discard can restore it visually.
   */
  storeResetChange(args: {
    elementId: string
    regionId: string
    sectionId: string
    originalMaterial: THREE.Material
    elementRef: StructuralElement
  }): void {
    const existing = this._pendingChanges.get(args.elementId)
    if (existing && existing.previewMaterial) {
      existing.previewMaterial.dispose()
    }
    this._pendingChanges.set(args.elementId, {
      elementId: args.elementId,
      regionId: args.regionId,
      sectionId: args.sectionId,
      materialData: null,
      previewMaterial: null,
      originalMaterial: args.originalMaterial,
      elementRef: args.elementRef,
      isReset: true,
    })
    this._pendingCount$.next(this._pendingChanges.size)
  }

  getPendingChange(elementId: string): PendingMaterialChange | null {
    return this._pendingChanges.get(elementId) ?? null
  }

  hasPendingChange(elementId: string): boolean {
    return this._pendingChanges.has(elementId)
  }

  removePendingChange(elementId: string): void {
    const existing = this._pendingChanges.get(elementId)
    if (!existing) return
    if (existing.previewMaterial) existing.previewMaterial.dispose()
    this._pendingChanges.delete(elementId)
    this._pendingCount$.next(this._pendingChanges.size)
  }

  getAllPendingChanges(): PendingMaterialChange[] {
    return Array.from(this._pendingChanges.values())
  }

  getUniqueRegionIds(): string[] {
    const regionIds = new Set<string>()
    for (const change of this._pendingChanges.values()) {
      regionIds.add(change.regionId)
    }
    return Array.from(regionIds)
  }

  promoteAllMaterials(): void {
    for (const change of this._pendingChanges.values()) {
      if (change.isReset) {
        change.elementRef.customMaterial = null
      } else {
        change.elementRef.customMaterial = change.previewMaterial
      }
    }
  }

  discardAllChanges(): void {
    for (const change of this._pendingChanges.values()) {
      change.elementRef.currentRenderMaterial = change.originalMaterial
      if (change.previewMaterial) change.previewMaterial.dispose()
    }
  }

  clear(): void {
    this._pendingChanges.clear()
    this._pendingCount$.next(0)
  }
}
